import MinhasBuscas from './pages/MinhasBuscas';
// src/App.js
import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Cadastro';
import AdminLayout from './components/AdminLayout';
// ComoAjudar / AssessoriaEstrategica saíram: viraram páginas estáticas
// (public/home e public/assessoria), servidas direto pelo Nginx.
import { ToastViewport } from './ui/notifications/toast';
import { startNotificationsEngine, stopNotificationsEngine } from './ui/notifications/engine';
import './index.css';
import RequireAuth from './components/RequireAuth';
import EducacaoAcessos from './pages/EducacaoAcessos';
import UserLayout from './components/UserLayout';
import { Navigate } from 'react-router-dom';

// Porta de entrada pública: se o visitante JÁ está logado, pula pra área dele
// (admin → /admin/dashboard; usuário → /dashboard). Senão mostra o conteúdo
// público passado (portal em / ou landing em /plataforma).
// Redireciona pra uma URL servida FORA do React (as páginas estáticas do Nginx).
// <Navigate> não serve: o Router tentaria casar uma rota interna e cairia no 404.
function RedirectExterno({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

// Cada pagina passa a ser baixada so quando alguem entra nela. Antes, um
// visitante do /register baixava as 44 telas do sistema de uma vez.
const Leiloes = lazy(() => import('./pages/Leiloes'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TodosLeiloes = lazy(() => import('./pages/TodosLeiloes'));
const LeiloesErro = lazy(() => import('./pages/LeiloesErro'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const FavoritosPage = lazy(() => import('./pages/FavoritosPage'));
const Calculadora = lazy(() => import('./pages/Calculadora'));
const MeusMateriais = lazy(() => import('./pages/MeusMateriais'));
const VitrineEducacao = lazy(() => import('./pages/VitrineEducacao'));
const AreaMembros = lazy(() => import('./pages/AreaMembros'));
const AdminMembros = lazy(() => import('./pages/AdminMembros'));
const VendasEducacao = lazy(() => import('./pages/VendasEducacao'));
const GestaoEducacao = lazy(() => import('./pages/GestaoEducacao'));
const RelatoriosEducacao = lazy(() => import('./pages/RelatoriosEducacao'));
const AdminMateriais = lazy(() => import('./pages/AdminMateriais'));
const MinhasAnalises = lazy(() => import('./pages/MinhasAnalises'));
const ComeceAqui = lazy(() => import('./pages/ComeceAqui'));
const PlataformaV3 = lazy(() => import('./pages/PlataformaV3'));
const AdminLanding = lazy(() => import('./pages/AdminLanding'));
const AdminLeads = lazy(() => import('./pages/AdminLeads'));
const CRM = lazy(() => import('./pages/CRM'));
const Depoimentos = lazy(() => import('./pages/Depoimentos'));
const Apresentacoes = lazy(() => import('./pages/Apresentacoes'));
const AdminLeila = lazy(() => import('./pages/AdminLeila'));
const AdminWhatsapp = lazy(() => import('./pages/AdminWhatsapp'));
const LeilaoDetalhe = lazy(() => import('./pages/LeilaoDetalhe'));
const Resultados = lazy(() => import('./pages/Resultados'));
const Privacidade = lazy(() => import('./pages/Privacidade'));
const Home = lazy(() => import('./pages/Home'));
const Painel = lazy(() => import('./pages/Painel'));
const Planos = lazy(() => import('./pages/Planos'));
const Usuarios = lazy(() => import('./pages/Usuarios'));
const BuscarLeiloes = lazy(() => import('./pages/BuscarLeiloes'));
const Importar = lazy(() => import('./pages/ImportarPlanilha'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const LeiloesCaixa = lazy(() => import('./pages/LeiloesCaixa'));
const ComoFinanciar = lazy(() => import('./pages/ComoFinanciar'));
const SobreLeilancer = lazy(() => import('./pages/SobreLeilancer'));
const Dicas = lazy(() => import('./pages/Dicas'));
const Contato = lazy(() => import('./pages/Contato'));
const MapaLeiloes = lazy(() => import('./pages/MapaLeiloes'));
const Leiloeiros = lazy(() => import('./pages/Leiloeiros'));
const AdminBanners = lazy(() => import('./pages/AdminBanners'));
const AdminLeiloes = lazy(() => import('./pages/AdminLeiloes'));
const GerenciadorImoveis = lazy(() => import('./pages/GerenciadorImoveis'));
const AdminAuditoria = lazy(() => import('./pages/AdminAuditoria'));
const NotFound = lazy(() => import('./pages/NotFound'));
const MinhaConta = lazy(() => import('./pages/MinhaConta'));
const MeusAlertas = lazy(() => import('./pages/MeusAlertas'));
const BemVindo = lazy(() => import('./pages/BemVindo'));
const PlanoExpirado = lazy(() => import('./pages/PlanoExpirado'));
const PagamentoConfirmado = lazy(() => import('./pages/PagamentoConfirmado'));
const Glossario = lazy(() => import('./pages/Glossario'));
const Ajuda = lazy(() => import('./pages/Ajuda'));
const Alertas = lazy(() => import('./pages/Alertas'));
const Benchmark = lazy(() => import('./pages/Benchmark'));
const LeiloeirosPublico = lazy(() => import('./pages/LeiloeirosPublico'));

// Aparece só no intervalo entre clicar num link e a tela dele chegar. Fica
// deliberadamente sóbrio: um spinner chamativo num piscar de olhos incomoda
// mais do que ajuda.
function TelaCarregando() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        fontSize: 14,
      }}
    >
      Carregando…
    </div>
  );
}

function GateHome({ children }) {
  let destino = null;
  try {
    if (sessionStorage.getItem('token')) {
      const user = JSON.parse(sessionStorage.getItem('user') || 'null');
      if (user) destino = user.role==='educacao'?'/admin/educacao/produtos':['admin', 'moderador'].includes(user.role) ? '/admin/dashboard' : '/dashboard';
    }
  } catch { /* ignora */ }
  return destino ? <Navigate to={destino} replace /> : children;
}

// A raiz "/" é uma página ESTÁTICA servida pelo Nginx, não uma rota do SPA.
// Só que o logo (e mais uns nove pontos) usa <Link to="/">, que navega pelo
// cliente e nunca chega ao servidor: a rota não existia aqui e o usuário caía
// no catch-all, vendo um 404 ao clicar no próprio logo.
//
// Quem está logado vai para o seu painel, que é o que se espera ao clicar no
// logo dentro do app. Quem não está recebe a home estática numa recarga de
// verdade — "/" não devolve o SPA, então não há risco de ficar recarregando.
function RaizHome() {
  let destino = null;
  try {
    if (sessionStorage.getItem('token')) {
      const user = JSON.parse(sessionStorage.getItem('user') || 'null');
      if (user) destino = user.role==='educacao'?'/admin/educacao/produtos':['admin', 'moderador'].includes(user.role) ? '/admin/dashboard' : '/dashboard';
    }
  } catch { /* ignora */ }

  useEffect(() => {
    if (!destino) window.location.replace('/');
  }, [destino]);

  return destino ? <Navigate to={destino} replace /> : <TelaCarregando />;
}

function App() {
  useEffect(() => {
    // O motor de notificações baixa 300 leilões (~400 KB) a cada minuto. Isso
    // só interessa a quem está logado — antes ele rodava em toda página, e no
    // /register competia com o próprio formulário pela banda do celular,
    // segurando a tela de cadastro por vários segundos.
    let logado = false;
    try { logado = !!sessionStorage.getItem('token'); } catch { /* sem storage */ }
    if (!logado) return undefined;

    startNotificationsEngine({
      apiBase: process.env.REACT_APP_API_URL,
      intervalMs: 60000,
      limit: 300,
    });
    return () => stopNotificationsEngine();
  }, []);

  return (
    <>
      <ToastViewport />
      <Suspense fallback={<TelaCarregando />}>
        <Routes>
        {/* ===== PÚBLICAS (sem login) — SOMENTE estas ===== */}
        {/* Landing (entrada do funil), Login/Cadastro, Planos e Privacidade.
            Todo o resto do conteúdo passou a exigir login (decisão do cliente). */}
        {/* Home ("/"), /kit, /obrigado, /metodo e /assessoria são páginas ESTÁTICAS
            servidas pelo Nginx (public/home, public/kit, ...) — não passam por aqui.
            O SPA segue dono do fallback de todo o resto. */}
        {/* "/" existe aqui só para atender os <Link to="/"> internos (o logo).
            Servida pelo Nginx quando digitada direto — veja RaizHome. */}
        <Route path="/" element={<RaizHome />} />

        {/* Landing comercial da plataforma */}
        <Route path="/plataforma" element={<GateHome><ComeceAqui /></GateHome>} />
        <Route path="/plataforma-v3" element={<GateHome><PlataformaV3 /></GateHome>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/planos" element={<Planos />} />
        {/* URL antiga da assessoria -> nova URL definitiva (estática) */}
        <Route path="/membro-assessoria-estrategica" element={<RedirectExterno to="/assessoria" />} />
        <Route path="/privacidade" element={<Privacidade />} />

        {/* ===== EXIGEM LOGIN — conteúdo institucional (antes público) =====
            Visitante sem login cai no /login (RequireAuth). */}
        <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/home-antiga" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/sobre" element={<RequireAuth><SobreLeilancer /></RequireAuth>} />
        <Route path="/contato" element={<RequireAuth><Contato /></RequireAuth>} />
        <Route path="/como-financiar" element={<RequireAuth><ComoFinanciar /></RequireAuth>} />
        <Route path="/dicas" element={<RequireAuth><Dicas /></RequireAuth>} />
        <Route path="/blog" element={<RequireAuth><Dicas /></RequireAuth>} />
        <Route path="/glossario" element={<RequireAuth><Glossario /></RequireAuth>} />
        <Route path="/ajuda" element={<RequireAuth><Ajuda /></RequireAuth>} />
        <Route path="/alertas" element={<RequireAuth><Alertas /></RequireAuth>} />
        <Route path="/benchmark" element={<RequireAuth><Benchmark /></RequireAuth>} />
        <Route path="/leiloeiros" element={<RequireAuth><LeiloeirosPublico /></RequireAuth>} />

        {/* Onboarding — telas de transição.
            /bem-vindo (1º acesso) e /plano-expirado exigem login;
            /pagamento-confirmado é público (destino de retorno do checkout). */}
        <Route path="/bem-vindo" element={<RequireAuth><BemVindo /></RequireAuth>} />
        <Route path="/plano-expirado" element={<RequireAuth><PlanoExpirado /></RequireAuth>} />
        <Route path="/pagamento-confirmado" element={<PagamentoConfirmado />} />

        {/* ===== ÁREA DO USUÁRIO (logado) — mesmo shell visual do admin ===== */}
        <Route path="/dashboard" element={<RequireAuth><UserLayout /></RequireAuth>}>
          <Route index element={<Painel />} />
          <Route path="buscar" element={<Leiloes embedded />} />
          <Route path="mapa" element={<MapaLeiloes embedded />} />
          <Route path="minhas-buscas" element={<MinhasBuscas />} />
          <Route path="favoritos" element={<FavoritosPage />} />
          <Route path="alertas" element={<MeusAlertas />} />
          <Route path="analises" element={<MinhasAnalises embedded />} />
          <Route path="analise/:id" element={<Calculadora embedded />} />
          <Route path="vitrine" element={<VitrineEducacao />} />
          <Route path="membros" element={<AreaMembros />} />
          <Route path="membros/:cursoId" element={<AreaMembros />} />
          <Route path="materiais" element={<MeusMateriais />} />
          <Route path="relatorios" element={<Relatorios />} />
          <Route path="conta" element={<MinhaConta />} />
        </Route>

        {/* Detalhe/calculadora continuam standalone (abrem em página cheia) */}
        <Route path="/calculadora/:id"   element={<RequireAuth><Calculadora /></RequireAuth>} />
        <Route path="/leilao/:id"        element={<RequireAuth><LeilaoDetalhe /></RequireAuth>} />
        <Route path="/leiloes-caixa"     element={<RequireAuth><LeiloesCaixa /></RequireAuth>} />
        <Route path="/resultados"        element={<RequireAuth><Resultados /></RequireAuth>} />

        {/* Links antigos → redirecionam pra dentro da nova área /dashboard */}
        <Route path="/leiloes"         element={<Navigate to="/dashboard/buscar" replace />} />
        <Route path="/mapa"            element={<Navigate to="/dashboard/mapa" replace />} />
        <Route path="/minhas-analises" element={<Navigate to="/dashboard/analises" replace />} />
        <Route path="/painel"          element={<Navigate to="/dashboard" replace />} />

        {/* Rotas Admin (com layout) — exige login + role admin/moderador.
            /admin/leiloeiros (lowercase) é a forma canônica; a forma com 'L'
            maiúsculo é mantida só pra compatibilidade com links antigos. */}
        <Route path="/admin" element={<RequireAuth role="admin"><AdminLayout /></RequireAuth>}>
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          {/* Buscar Imóveis no admin: mesmo tema CLARO da área do usuário
              (a shell do admin é escura; sem isto a página fica preta e os
              filtros com cor errada). Margem negativa cancela o padding escuro
              do conteúdo admin pra ficar igual ao /dashboard/buscar. */}
          <Route path="buscar" element={
            <div className="-m-4 md:-m-6 min-h-full bg-gray-50 text-gray-900">
              <Leiloes embedded />
            </div>
          } />
          <Route path="leiloeiros" element={<Leiloeiros />} />
          <Route path="buscar-leiloes" element={<BuscarLeiloes />} />
          <Route path="leiloes" element={<AdminLeiloes />} />
          <Route path="gerenciador" element={<GerenciadorImoveis />} />
          {/* As análises salvas existiam só no menu do usuário (/dashboard),
              então quem trabalha pelo painel administrativo não achava as
              próprias análises. Mesmo tratamento do "buscar": a tela é de tema
              claro e a shell do admin é escura, e a margem negativa cancela o
              padding do conteúdo pra não sobrar uma moldura. */}
          <Route path="analises" element={
            <div className="-m-4 md:-m-6 min-h-full bg-gray-50 text-gray-900">
              <MinhasAnalises embedded />
            </div>
          } />
          <Route path="analise/:id" element={
            <div className="-m-4 md:-m-6 min-h-full bg-gray-50 text-gray-900">
              <Calculadora embedded />
            </div>
          } />
          <Route path="auditoria" element={<AdminAuditoria />} />
          <Route path="usuarios" element={<Usuarios />} />
          <Route path="banners" element={<AdminBanners />} />
          <Route path="membros" element={<RequireAuth role="educacao"><AdminMembros /></RequireAuth>} />
          <Route path="educacao/acessos" element={<RequireAuth role="admin-strict"><EducacaoAcessos /></RequireAuth>} />
          <Route path="educacao/vitrine" element={<RequireAuth role="educacao"><VitrineEducacao /></RequireAuth>} />
          <Route path="educacao/vendas" element={<RequireAuth role="educacao"><VendasEducacao /></RequireAuth>} />
          <Route path="educacao/produtos" element={<RequireAuth role="educacao"><GestaoEducacao key="produtos" tipo="produtos" /></RequireAuth>} />
          <Route path="educacao/produtos/novo" element={<RequireAuth role="educacao"><GestaoEducacao key="novo" tipo="produtos" novo /></RequireAuth>} />
          <Route path="educacao/contratos" element={<RequireAuth role="educacao"><GestaoEducacao key="contratos" tipo="contratos" /></RequireAuth>} />
          <Route path="educacao/leads-educacao" element={<RequireAuth role="educacao"><GestaoEducacao key="leads" tipo="leads" /></RequireAuth>} />
          <Route path="educacao/relatorios-educacao" element={<RequireAuth role="educacao"><RelatoriosEducacao /></RequireAuth>} />
          <Route path="membros/:cursoId" element={<AreaMembros />} />
          <Route path="materiais" element={<AdminMateriais />} />
          <Route path="importar" element={<Importar />} />
          <Route path="configuracoes" element={<Configuracoes />} />
          <Route path="relatorios" element={<Relatorios />} />
          <Route path="landing" element={<AdminLanding />} />
          <Route path="mapa" element={<MapaLeiloes embedded />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="crm" element={<RequireAuth role="admin-strict"><CRM /></RequireAuth>} />
          <Route path="apresentacoes" element={<RequireAuth role="admin-strict"><Apresentacoes /></RequireAuth>} />
          <Route path="depoimentos" element={<RequireAuth role="admin-strict"><Depoimentos /></RequireAuth>} />
          <Route path="leila" element={<AdminLeila />} />
          <Route path="whatsapp" element={<AdminWhatsapp />} />
          <Route path="minhas-buscas" element={<MinhasBuscas />} />
          <Route path="favoritos" element={<FavoritosPage />} />
        </Route>

        {/* Rotas antigas do admin (mantidas para compatibilidade) — exigem login */}
        <Route path="/todos-leiloes" element={<RequireAuth><TodosLeiloes /></RequireAuth>} />
        <Route path="/leiloes-erro" element={<RequireAuth><LeiloesErro /></RequireAuth>} />
        <Route path="/relatorios" element={<RequireAuth><Relatorios /></RequireAuth>} />
        <Route path="/favoritos" element={<RequireAuth><FavoritosPage /></RequireAuth>} />

        {/* Catch-all 404 — qualquer URL que não casa cai aqui */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
